import { request } from './api'

// The vendor registry's settings screen talks to these. The contract other
// systems read is docs/interop/vendor-registry-v1.md; this is only the part a
// person in the console configures. See
// apps/tprm-api/internal/api/interop_registry_console.go.

export type RegistryEventType =
  | 'vendor.upserted'
  | 'vendor.offboarded'
  | 'agreement.upserted'
  | 'processing.updated'
  | 'incident.recorded'
  | 'incident.updated'

// The incident kinds carry what a vendor breach exposes, so a destination gets
// them only when somebody ticks them (contract section 11).
export const INCIDENT_EVENTS: readonly RegistryEventType[] = ['incident.recorded', 'incident.updated']

export type RegistrySubscriber = {
  id: string
  name: string
  // WEBHOOK is another system; FOCTTA_SUITE is FOCTTA Core, which sets its own.
  kind: 'WEBHOOK' | 'FOCTTA_SUITE'
  url: string
  eventTypes: RegistryEventType[]
  enabled: boolean
  lastDeliveredAt?: string
  lastFailedAt?: string
  lastError?: string
  createdBy: string
  createdAt: string
  updatedAt: string
  waiting: number
  sent: number
  failing: number
  dead: number
}

export type RegistryDelivery = {
  id: string
  eventType: RegistryEventType
  eventId: string
  vendorId: string
  vendorCode: string
  vendorName: string
  cause: 'CHANGE' | 'RESYNC'
  status: 'PENDING' | 'SENDING' | 'SENT' | 'FAILED' | 'DEAD'
  attempts: number
  queuedAt: string
  nextAttemptAt: string
  lastAttemptAt?: string
  lastHttpStatus?: number
  lastError?: string
  sentAt?: string
}

export type RegistryRequest = {
  requestId: string
  externalRequestId: string
  sourceSystem: string
  vendorId: string
  vendorCode: string
  legalName: string
  status: string
  matchedBy: 'CREATED' | 'EXTERNAL_REFERENCE' | 'PAN' | 'GSTIN' | 'CIN' | 'LLPIN' | 'LEI'
  vendorCreated: boolean
  receivedAt: string
  lastReceivedAt: string
  receiveCount: number
}

export type RegistryTest = {
  answer: 'ok' | 'refused' | 'unproven'
  status: string
  httpStatus?: number
  detail: string
}

const base = '/v1/vendor-registry'

export const registryApi = {
  subscribers: () =>
    request<{
      subscribers: RegistrySubscriber[]
      eventTypes: RegistryEventType[]
      contractVersion: string
      pairedWithCore: boolean
      suiteSigning: boolean
    }>(base + '/subscribers'),
  addSubscriber: (body: { name: string; url: string; eventTypes: RegistryEventType[] }) =>
    request<{
      subscriber: RegistrySubscriber
      signingSecret: string
      how: string
      note: string
    }>(base + '/subscribers', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  changeSubscriber: (
    id: string,
    body: {
      name?: string
      url?: string
      eventTypes?: RegistryEventType[]
      enabled?: boolean
    },
  ) =>
    request<{ subscriber: RegistrySubscriber }>(base + '/subscribers/' + encodeURIComponent(id), {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  testSubscriber: (id: string) =>
    request<RegistryTest>(base + '/subscribers/' + encodeURIComponent(id) + '/test', { method: 'POST' }),
  resync: (id: string) =>
    request<{ queued: number; note: string }>(base + '/subscribers/' + encodeURIComponent(id) + '/resync', {
      method: 'POST',
    }),
  retry: (id: string) =>
    request<{ requeued: number }>(base + '/subscribers/' + encodeURIComponent(id) + '/retry', { method: 'POST' }),
  deliveries: (id: string) =>
    request<{ deliveries: RegistryDelivery[] }>(
      base + '/subscribers/' + encodeURIComponent(id) + '/deliveries?limit=20',
    ),
  requests: () => request<{ requests: RegistryRequest[] }>(base + '/requests?limit=20'),
}

// What each kind of change is called on the screen.
export const EVENT_WORDS: Record<RegistryEventType, string> = {
  'vendor.upserted': 'A vendor changes',
  'vendor.offboarded': 'A vendor is offboarded',
  'agreement.upserted': 'An agreement changes',
  'processing.updated': 'What a vendor processes changes',
  'incident.recorded': 'An incident is recorded at a vendor',
  'incident.updated': 'A vendor incident changes, or a duty on it is met',
}

// What each kind of change is called where a list of them is read as a
// sentence: "Sends vendor changes, agreements and processing".
export const EVENT_NOUNS: Record<RegistryEventType, string> = {
  'vendor.upserted': 'vendor changes',
  'vendor.offboarded': 'offboarding',
  'agreement.upserted': 'agreements',
  'processing.updated': 'processing',
  'incident.recorded': 'incidents',
  'incident.updated': 'incident changes',
}

// One delivery, named by what it carried.
export const DELIVERY_NOUNS: Record<RegistryEventType, string> = {
  'vendor.upserted': 'Vendor record',
  'vendor.offboarded': 'Offboarding',
  'agreement.upserted': 'Agreement',
  'processing.updated': 'Processing',
  'incident.recorded': 'Incident',
  'incident.updated': 'Incident change',
}

// "a, b and c".
export function listWords(words: string[]): string {
  if (words.length <= 1) return words.join('')
  return words.slice(0, -1).join(', ') + ' and ' + words[words.length - 1]
}

// The scopes a key for the register may carry, in the words the screen uses.
// The credential itself is the procurement credential. incident.report is a
// breach tool telling this product a duty on a vendor incident was met.
export const REGISTRY_SCOPES = ['registry.read', 'vendor.request', 'incident.report'] as const

export function scopeWords(scope: string): string {
  switch (scope) {
    case 'intake':
      return 'File requests'
    case 'clearance':
      return 'Look up'
    case 'registry.read':
      return 'Read vendors'
    case 'vendor.request':
      return 'Ask for vendors'
    case 'incident.report':
      return 'Say a breach duty was met'
  }
  return scope
}

// A key's abilities as one sentence: "Read vendors and ask for vendors".
export function scopesSentence(scopes: string[]): string {
  const s = listWords(scopes.map((x) => scopeWords(x).toLowerCase()))
  return s.charAt(0).toUpperCase() + s.slice(1)
}
